import React from 'react';
import { Link } from 'react-router-dom';
import useSiteConfig, { PLACEHOLDER } from '../hooks/useSiteConfig';
import { Building2, Phone, Mail, MapPin } from 'lucide-react';

const Footer = () => {
  const c = useSiteConfig();
  return (
    <footer className="bg-muted/50 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-foreground">FraxLandShare</span>
            </div>
            <p className="text-muted-foreground">
              Making real estate investment accessible through fractional ownership of premium Delhi land parcels.
            </p>
            <div className="space-y-2">
              <div className="flex items-center text-muted-foreground">
                <Phone className="w-4 h-4 mr-2" />
                <span className="text-sm">{c.support_phone || PLACEHOLDER}</span>
              </div>
              <div className="flex items-center text-muted-foreground">
                <Mail className="w-4 h-4 mr-2" />
                <span className="text-sm">{c.support_email || PLACEHOLDER}</span>
              </div>
            </div>
          </div>

          {/* Company */}
          <div>
            <h2 className="font-semibold text-foreground mb-4">Company</h2>
            <ul className="space-y-3">
              <li><Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors">Contact</Link></li>
              <li><Link to="/about" className="text-muted-foreground hover:text-primary transition-colors">About Us</Link></li>
              <li><a href="#how-it-works" className="text-muted-foreground hover:text-primary transition-colors" onClick={(e) => { e.preventDefault(); document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' }); }}>How it Works</a></li>
              <li><Link to="/faq" className="text-muted-foreground hover:text-primary transition-colors">FAQ</Link></li>
            </ul>
          </div>

          {/* Investment */}
          <div>
            <h2 className="font-semibold text-foreground mb-4">Investment</h2>
            <ul className="space-y-3">
              <li><Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors">Request an invite</Link></li>
              <li><Link to="/investment-guide" className="text-muted-foreground hover:text-primary transition-colors">Investment Guide</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h2 className="font-semibold text-foreground mb-4">Legal</h2>
            <ul className="space-y-3">
              <li><Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">Terms of Service</Link></li>
              <li><Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">Privacy Policy</Link></li>
              <li><Link to="/risk-disclosure" className="text-muted-foreground hover:text-primary transition-colors">Risk Disclosure</Link></li>
              <li><Link to="/mca-compliant" className="text-muted-foreground hover:text-primary transition-colors">MCA Compliant</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-border mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-muted-foreground text-sm mb-4 md:mb-0">
              © {new Date().getFullYear()} {c.operator_name || PLACEHOLDER}. CIN: {c.operator_cin || PLACEHOLDER}.
            </div>
            <div className="flex items-center text-muted-foreground text-sm">
              <MapPin className="w-4 h-4 mr-1" />
              <span>{c.operator_address || 'New Delhi, India'}</span>
            </div>
          </div>

          {/* Risk Warning */}
          <div className="mt-6 text-xs text-muted-foreground bg-muted/50 p-4 rounded-lg">
            <strong>Investment Attention – Real Estate</strong>
            <p className="mt-2">
              Real estate investments, including fractional ownership, are subject to market, regulatory, and liquidity risks. While land and property have historically been considered relatively stable, safe, and long-term wealth-building assets, returns are not guaranteed and may vary based on market conditions, government policies, and uncertainty or delays in project execution.
            </p>
            <p className="mt-2">
              Investors should carefully review all legal and financial documents, understand exit timelines, and assess personal risk appetite before investing. Unlike volatile financial instruments, real estate offers the strength of tangible ownership, potential for capital appreciation, and a natural hedge against inflation, but investors must be aware of possible challenges such as limited liquidity or delayed project outcomes.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
